import React, { useState, useEffect } from 'react';
import { Modal, Input, Radio, Space, message } from 'antd';
import { useUpdateProcessingIssueMutation } from '../../../redux/api/compassPortal';
import SourceCommentThread from '../SourceCommentThread';

const ProcessingIssueModal = ({ open, onClose, source, projectId, isAdmin, isInternal }) => {
    const [issueText, setIssueText] = useState('');
    const [action, setAction] = useState('raise'); // "raise" | "resolve" | "clear"
    const [updateIssue, { isLoading }] = useUpdateProcessingIssueMutation();

    useEffect(() => {
        if (source && open) {
            if (source.processing_issue_status === 'open') {
                setAction('resolve');
                setIssueText(source.processing_issue || '');
            } else if (source.processing_issue_status === 'resolved') {
                setAction('raise');
                setIssueText('');
            } else {
                setAction('raise');
                setIssueText('');
            }
        }
    }, [source, open]);

    const handleOk = async () => {
        try {
            let status = null;
            let text = issueText;

            if (action === 'raise') {
                status = 'open';
            } else if (action === 'resolve') {
                status = 'resolved';
            } else {
                status = null;
                text = '';
            }

            await updateIssue({
                projectId,
                sourceId: source.source_id,
                issue_text: text,
                status,
            }).unwrap();
            onClose();
        } catch (err) {
            message.error('Failed to update processing issue');
        }
    };

    if (!source) return null;

    const hasOpenIssue = source.processing_issue_status === 'open';
    const hasResolvedIssue = source.processing_issue_status === 'resolved';
    const canModifyIssue = isInternal || isAdmin;

    return (
        <Modal
            title={`Processing Issue: ${source.name}`}
            open={open}
            onOk={canModifyIssue ? handleOk : undefined}
            onCancel={onClose}
            confirmLoading={isLoading}
            okText={canModifyIssue ? (action === 'raise' ? 'Raise Issue' : action === 'resolve' ? 'Resolve' : 'Clear') : undefined}
            footer={canModifyIssue ? undefined : null}
            width={600}
        >
            {/* Issue controls — internal/admin only */}
            {canModifyIssue && (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Radio.Group value={action} onChange={(e) => setAction(e.target.value)}>
                        {!hasOpenIssue && (
                            <Radio value="raise">Raise New Issue</Radio>
                        )}
                        {hasOpenIssue && (
                            <Radio value="resolve">Resolve Issue</Radio>
                        )}
                        {(hasOpenIssue || hasResolvedIssue) && (
                            <Radio value="clear">Clear Issue</Radio>
                        )}
                    </Radio.Group>

                    {action === 'raise' && (
                        <Input.TextArea
                            rows={3}
                            value={issueText}
                            onChange={(e) => setIssueText(e.target.value)}
                            placeholder="Describe the processing issue..."
                        />
                    )}

                    {action === 'resolve' && source.processing_issue && (
                        <div style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: 4 }}>
                            <strong>Current issue:</strong> {source.processing_issue}
                        </div>
                    )}
                </Space>
            )}

            {/* Read-only issue display for clients */}
            {!canModifyIssue && source.processing_issue && (
                <>
                    <div style={{ fontSize: 13, color: 'var(--medium-light-grey-color)', marginBottom: 10 }}>
                        The processing team has flagged an issue with this data source. Please review the details below and respond in the comments.
                    </div>
                    <div style={{ padding: '10px 14px', background: 'var(--tertiary-color)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 6, marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: source.processing_issue_status === 'open' ? '#f59e0b' : 'var(--green-color)', textTransform: 'uppercase', marginBottom: 4, fontWeight: 600 }}>
                            {source.processing_issue_status === 'open' ? 'Open Issue' : 'Resolved'}
                        </div>
                        <div style={{ fontSize: 14, color: 'var(--white-color)' }}>{source.processing_issue}</div>
                    </div>
                </>
            )}

            {!canModifyIssue && !source.processing_issue && (
                <div style={{ color: 'var(--medium-light-grey-color)', fontSize: 13, padding: '8px 0' }}>
                    No processing issues reported. You can use the comments below to discuss this data source with the team.
                </div>
            )}

            {/* Comment thread — visible to everyone */}
            <SourceCommentThread
                projectId={projectId}
                sourceId={source.source_id}
                isAdmin={isAdmin}
            />
        </Modal>
    );
};

export default ProcessingIssueModal;
